import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Loader2, ImagePlus, X, Building2 } from 'lucide-react';

/**
 * Shared chat input — single integrated box with:
 * - Textarea
 * - Icon row: image upload, model selector, send (bottom)
 * - Company selection is handled by the shared CompanyTopBar
 */
export function ChatInput({
  onSend, loading, placeholder, disabled,
  selectedCompanies, onSelectCompany, onRemoveCompany,
  models, selectedModel, onModelChange,
}) {
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isVisionModel = (selectedModel || '').toLowerCase().includes('gemini');
  const hasCompanyProps = selectedCompanies && onSelectCompany && onRemoveCompany;
  const hasModelProps = models && selectedModel !== undefined && onModelChange;

  useEffect(() => {
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  }, [message]);

  const addImage = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024 || images.length >= 5) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImages(prev => [...prev, { base64: dataUrl.split(',')[1], mime_type: file.type, preview: dataUrl }]);
    };
    reader.readAsDataURL(file);
  }, [images.length]);

  const handlePaste = useCallback((e) => {
    if (!isVisionModel) return;
    for (const item of (e.clipboardData?.items || [])) {
      if (item.type.startsWith('image/')) { e.preventDefault(); addImage(item.getAsFile()); return; }
    }
  }, [isVisionModel, addImage]);

  const handleDrop = useCallback((e) => {
    if (!isVisionModel) return;
    e.preventDefault();
    for (const file of e.dataTransfer.files) { if (file.type.startsWith('image/')) addImage(file); }
  }, [isVisionModel, addImage]);

  const noCompanies = hasCompanyProps && selectedCompanies.length === 0;

  const handleSend = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed || loading || disabled || noCompanies) return;
    onSend(trimmed, images.length > 0 ? images : undefined);
    setMessage('');
    setImages([]);
  }, [message, images, loading, disabled, noCompanies, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <div className="flex-shrink-0 border-t border-zinc-200 bg-white p-3">
      <div
        className="bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 relative"
        onDragOver={isVisionModel ? (e) => e.preventDefault() : undefined}
        onDrop={isVisionModel ? handleDrop : undefined}
      >
        {/* No company selected warning */}
        {hasCompanyProps && selectedCompanies.length === 0 && (
          <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-zinc-200 text-[11px] text-amber-600">
            <Building2 size={12} />
            Select companies from the top bar to start chatting
          </div>
        )}

        {/* Image previews */}
        {images.length > 0 && (
          <div className="flex gap-1.5 mb-1.5 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <img src={img.preview} alt="" className="w-14 h-14 object-cover rounded-md border border-zinc-200" />
                <button onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-zinc-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder || "Ask a question…"}
          rows={1}
          className="w-full bg-transparent text-zinc-800 text-[13px] resize-none focus:outline-none placeholder-zinc-400"
          style={{ minHeight: '24px', maxHeight: '120px' }}
          disabled={loading || disabled || noCompanies}
        />

        {/* Bottom icon row */}
        <div className="flex items-center gap-1.5 mt-1">
          {/* Image upload */}
          {isVisionModel && (
            <>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { for (const f of e.target.files) addImage(f); e.target.value = ''; }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={loading || images.length >= 5}
                className="p-1 text-zinc-400 hover:text-accent disabled:text-zinc-300 rounded transition-colors flex-shrink-0" title="Attach image">
                <ImagePlus size={14} />
              </button>
            </>
          )}

          {/* Model selector */}
          {hasModelProps && (
            <>
              {isVisionModel && <span className="w-px h-4 bg-zinc-200 flex-shrink-0" />}
              <select value={selectedModel} onChange={e => onModelChange(e.target.value)}
                className="bg-transparent text-[11px] text-zinc-400 focus:outline-none cursor-pointer flex-1 min-w-0 truncate">
                {(models || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </>
          )}

          {/* Spacer */}
          {!hasModelProps && <div className="flex-1" />}

          {/* Send */}
          <button onClick={handleSend} disabled={!message.trim() || loading || disabled || noCompanies}
            className="p-1 text-zinc-400 hover:text-accent disabled:text-zinc-300 transition-colors flex-shrink-0">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}
